import { z } from "zod";
import { localized } from "../../../locales";

/**
 * A partnership invitation code as one organisation hands it to another: `XXXX-XXXXXXXX`.
 *
 * Both halves are `[A-Z0-9]`. The check id is the last four characters of the organisation id,
 * reversed and uppercased; the code itself is eight characters written by one of two producers —
 * `generateOTP` cuts it from a uuid and is therefore hex, while the `generate_8_digit_code()`
 * column default writes digits. Trim and uppercase run before the regex, so a pasted lowercase
 * code normalizes on every consumer of this schema, the server's validator included. The
 * overwrite inserts the hyphen the wire format carries: the web's OTP-style input holds twelve
 * plain characters and cannot type one, and the server splits on it. The connect body and the
 * preview's path parameter both use this one chain, so a code the preview accepts is a code the
 * connect accepts.
 */
export const connectCodeSchema = z
	.string()
	.trim()
	.toUpperCase()
	.overwrite((value) =>
		/^[A-Z0-9]{12}$/.test(value) ? `${value.slice(0, 4)}-${value.slice(4)}` : value,
	)
	.regex(/^[A-Z0-9]{4}-[A-Z0-9]{8}$/, {
		error: localized("organization.invitation_code_should_look_like"),
	});

export const postConnectOrganizationSchema = z.object({
	code: connectCodeSchema,
});

export type PostConnectOrganizationSchema = z.infer<typeof postConnectOrganizationSchema>;
